import { useState, useEffect } from 'react';
import { api, configureApi } from '../api/client';
import { useAuth } from '../stores/auth';
import { useTelegram } from '../hooks/useTelegram';
import { toast } from '../stores/toasts';
import type { Workshop } from '../types';

interface AuthGateProps {
  children: React.ReactNode;
}

export function AuthGate({ children }: AuthGateProps) {
  const auth = useAuth();
  const tg = useTelegram();
  const [bootstrapping, setBootstrapping] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    configureApi({
      getToken: () => useAuth.getState().accessToken,
      getActiveWorkshopId: () => useAuth.getState().activeWorkshop?.id ?? null,
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!tg.isReady) return;
      if (auth.accessToken && auth.user) {
        await refreshWorkshops();
        if (mounted) setBootstrapping(false);
        return;
      }
      try {
        if (!tg.initData) {
          throw new Error('Telegram initData mavjud emas');
        }
        const r = await api.telegramAuth(tg.initData);
        auth.setAuth({ accessToken: r.accessToken, refreshToken: r.refreshToken, user: r.user });
        await refreshWorkshops();
        if (mounted) setBootstrapping(false);
      } catch (e: any) {
        if (mounted) {
          setError(e?.message || 'Avtorizatsiya xatosi');
          setBootstrapping(false);
        }
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tg.isReady, tg.initData]);

  async function refreshWorkshops() {
    try {
      const list = await api.myWorkshops();
      auth.setWorkshops(list as Workshop[]);
      const current = useAuth.getState().activeWorkshop;
      if (!current && list.length > 0) {
        const first = list[0] as Workshop;
        auth.setActiveWorkshop(first);
        try {
          await api.switchWorkshop(first.id);
        } catch {
          // noop
        }
      } else if (current) {
        const exists = list.find((w: Workshop) => w.id === current.id);
        if (!exists && list.length > 0) {
          auth.setActiveWorkshop(list[0] as Workshop);
        }
      }
    } catch (e: any) {
      toast.error('Ustalar ro\'yxatini yuklab bo\'lmadi');
    }
  }

  if (bootstrapping) {
    return (
      <div className="app">
        <div className="app-body">
          <div className="spinner" />
          <div className="empty">Yuklanmoqda...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="app">
        <div className="app-body">
          <div className="empty">
            <div className="emoji">⚠️</div>
            <div>{error}</div>
            <div className="muted" style={{ marginTop: 8 }}>
              Telegram Mini App orqali kiring
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
